import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { recordDocView } from 'redux/documentation/docViewSlice';
import { FaSync, FaTimesCircle, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { FiPackage, FiEdit3, FiTool, FiShield, FiAlertTriangle, FiStar, FiSettings, FiBarChart2 } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { debounce } from 'helpers/debounce';
import LoadingSpinner    from 'components/common/LoadingSpinner';
import Button            from 'components/common/Button';
import ChangelogAdminPanel from 'components/app/documentation/ChangelogAdminPanel';
import DocViewStatsPanel from 'components/app/documentation/DocViewStatsPanel';

// Strip stored "v" prefix so `v${fmtV(version)}` never produces "vv3.0.9"
const fmtV = (v) => (v || '').replace(/^v/i, '');

// ── Section config ────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'added',         label: 'Added',       Icon: FiPackage,     accent: 'tw-border-l-emerald-500', iconBg: 'tw-bg-emerald-50', iconColor: 'tw-text-emerald-600', countKey: 'added_count'         },
  { key: 'modifications', label: 'Changed',     Icon: FiEdit3,       accent: 'tw-border-l-blue-500',    iconBg: 'tw-bg-blue-50',    iconColor: 'tw-text-blue-600',    countKey: 'modifications_count' },
  { key: 'fixed',         label: 'Fixed',       Icon: FiTool,        accent: 'tw-border-l-amber-500',   iconBg: 'tw-bg-amber-50',   iconColor: 'tw-text-amber-600',   countKey: 'fixed_count'         },
  { key: 'security',      label: 'Security',    Icon: FiShield,      accent: 'tw-border-l-red-500',     iconBg: 'tw-bg-red-50',     iconColor: 'tw-text-red-600',     countKey: 'security_count'      },
  { key: 'deprecated',    label: 'Deprecated',  Icon: FiAlertTriangle, accent: 'tw-border-l-slate-400', iconBg: 'tw-bg-slate-50',   iconColor: 'tw-text-slate-500',   countKey: null                  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const VersionItem = ({ entry, isActive, onClick }) => {
  const { version, release_date_formatted, is_latest, summary = {} } = entry.attributes;
  const total = (summary.added_count || 0) + (summary.modifications_count || 0) + (summary.fixed_count || 0);

  return (
    <button
      onClick={onClick}
      className={`tw-w-full tw-min-w-[218px] md:tw-min-w-0 tw-text-left tw-px-4 md:tw-px-5 tw-py-4 tw-transition-colors tw-duration-150 tw-flex tw-flex-col tw-gap-2.5 tw-border-b tw-border-r md:tw-border-r-0 tw-border-slate-200 hover:tw-bg-blue-50/50 last:tw-border-0 ${
        isActive
          ? 'tw-bg-blue-50/70 tw-border-l-4 tw-border-l-blue-600 tw-pl-3 md:tw-pl-4'
          : 'tw-border-l-4 tw-border-l-transparent hover:tw-border-l-blue-300'
      }`}
    >
      {/* Version & Badge Row */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span className={`tw-font-mono tw-text-sm tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-md tw-bg-gradient-to-r ${
          isActive 
            ? 'tw-from-blue-200 tw-to-blue-100 tw-text-blue-800 tw-border tw-border-blue-300/50' 
            : 'tw-from-slate-100 tw-to-slate-50 tw-text-slate-700 tw-border tw-border-slate-200/50'
        }`}>
          v{fmtV(version)}
        </span>
        {is_latest && (
          <span className="tw-px-2.5 tw-py-1 tw-bg-emerald-100 tw-text-emerald-700 tw-text-[10px] tw-font-bold tw-rounded-xl tw-uppercase tw-tracking-wide tw-whitespace-nowrap tw-shrink-0 tw-border tw-border-emerald-200/50">
            Latest
          </span>
        )}
      </div>

      {/* Date */}
      <div className="tw-text-xs tw-text-slate-400 tw-font-medium">{release_date_formatted}</div>

      {/* Summary badges */}
      {total > 0 && (
        <div className="tw-flex tw-gap-2 tw-flex-wrap">
          {summary.added_count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-emerald-50 tw-text-emerald-700 tw-text-[10px] tw-font-semibold tw-rounded-md tw-border tw-border-emerald-200/50">
              <span>+</span>{summary.added_count}
            </span>
          )}
          {summary.modifications_count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-blue-50 tw-text-blue-700 tw-text-[10px] tw-font-semibold tw-rounded-md tw-border tw-border-blue-200/50">
              <span>~</span>{summary.modifications_count}
            </span>
          )}
          {summary.fixed_count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-amber-50 tw-text-amber-700 tw-text-[10px] tw-font-semibold tw-rounded-md tw-border tw-border-amber-200/50">
              <span>✓</span>{summary.fixed_count}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

VersionItem.propTypes = {
  entry: PropTypes.shape({
    attributes: PropTypes.shape({
      version: PropTypes.string,
      release_date_formatted: PropTypes.string,
      is_latest: PropTypes.bool,
      summary: PropTypes.shape({
        added_count: PropTypes.number,
        modifications_count: PropTypes.number,
        fixed_count: PropTypes.number
      })
    }).isRequired
  }).isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func
};

const SectionCard = ({ section, items }) => {
  if (!items?.length) return null;
  const { label, Icon, accent, iconBg, iconColor } = section;

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-border-l-4 ${accent} tw-overflow-hidden tw-shadow-sm`}>
      <div className="tw-px-4 md:tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-200 tw-bg-slate-50/70 tw-flex tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className={`tw-p-2 tw-rounded ${iconBg} tw-flex tw-items-center tw-justify-center`}>
            <Icon className={`${iconColor}`} size={16} />
          </div>
          <span className="tw-text-sm tw-font-semibold tw-text-slate-800">{label}</span>
        </div>
        <span className="tw-px-2.5 tw-py-1 tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-bold tw-rounded-xl">
          {items.length}
        </span>
      </div>
      <ul className="tw-px-4 md:tw-px-5 tw-py-4 tw-space-y-2.5">
        {items.map((item, idx) => (
          <li key={idx} className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-text-slate-600 tw-leading-relaxed">
            <span className={`${iconColor} tw-shrink-0 tw-mt-1.5 tw-text-lg`}>●</span>
            <span className="tw-flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

SectionCard.propTypes = {
  section: PropTypes.shape({
    label: PropTypes.string,
    Icon: PropTypes.elementType,
    accent: PropTypes.string,
    iconBg: PropTypes.string,
    iconColor: PropTypes.string
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.node)
};

// ── Main component ────────────────────────────────────────────────────────────

const ChangelogPage = () => {
  const { version } = useParams();
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const isAdmin     = useSelector(s => s.profile_user?.data?.is_admin === true);
  const [entries, setEntries]             = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [search, setSearch]               = useState('');
  const [filter, setFilter]               = useState('all');
  const [showAdmin, setShowAdmin]         = useState(false);
  const [showStats, setShowStats]         = useState(false);

  const fetchChangelog = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?per_page=50&q=${encodeURIComponent(q)}` : '?per_page=50';
      const response = await apiAxios.get(`/changelog_entries${params}`);
      const items = response.data?.data || [];
      setEntries(items);
      if (items.length > 0) setSelectedEntry((current) => current || items[0]);
    } catch (err) {
      // silent — empty state handles it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChangelog(); }, [fetchChangelog]);

  useEffect(() => {
    if (version && entries.length > 0) {
      const entry = entries.find(e => e.attributes.version === version);
      if (entry) setSelectedEntry(entry);
    }
  }, [version, entries]);

  // Track a view of the changelog entry currently being read (per version, per day).
  useEffect(() => {
    const attrs = selectedEntry?.attributes;
    if (!attrs?.version) return;
    dispatch(recordDocView({
      docType: 'changelog',
      docKey:  attrs.version,
      docTitle: `v${fmtV(attrs.version)}${attrs.title ? ` — ${attrs.title}` : ''}`,
    }));
    // one view per version: the title is only a label, and a refetch hands back a new attributes object for the same version
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedEntry?.attributes?.version]);

  const debouncedSearch = useMemo(
    () => debounce((q) => fetchChangelog(q), 400),
    [fetchChangelog]
  );

  const handleSearchChange = (val) => {
    setSearch(val);
    debouncedSearch(val);
  };

  const handleSelect = (entry) => {
    setSelectedEntry(entry);
    navigate(`/documentation/changelog/${entry.attributes.version}`, { replace: true });
  };

  // Filter by type client-side (already filtered by q server-side)
  const filteredEntries = entries.filter(entry => {
    const a = entry.attributes;
    if (filter === 'features') return (a.added || []).length > 0;
    if (filter === 'fixes')    return (a.fixed || []).length > 0;
    return true;
  });

  const attrs = selectedEntry?.attributes;

  return (
    <div className="tw-min-h-screen tw-overflow-x-hidden tw-bg-[#f4f7fb]">
      <div className="tw-max-w-[1440px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-5 sm:tw-py-7 lg:tw-py-9">

        {/* ── Page header ── */}
        <div className="tw-mb-5 sm:tw-mb-7 tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-justify-between tw-gap-5">
          <div className="tw-flex tw-items-start tw-gap-3">
            <div className="tw-w-1.5 tw-h-12 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #2563eb, #6366f1)' }} />
            <div className="tw-flex tw-items-start tw-gap-2.5">
              <div className="tw-mt-1 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-blue-200 tw-bg-blue-50 tw-text-blue-600">
                <FiPackage size={19} />
              </div>
              <div>
                <h1 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-mb-0">Changelog</h1>
                <p className="tw-max-w-2xl tw-text-sm tw-leading-6 tw-text-slate-500 tw-mt-1 tw-mb-0">
                  Track all updates, features, and improvements to Pebsteel Portal
                </p>
              </div>
            </div>
          </div>
          {isAdmin && (
            <div className="tw-flex tw-w-full sm:tw-w-auto tw-flex-wrap tw-items-center tw-gap-2">
              <Button
                variant={showStats ? 'primary' : 'ghost'}
                size="md"
                onClick={() => { setShowStats(v => !v); setShowAdmin(false); }}
              >
                <FiBarChart2 size={14} />
                {showStats ? 'Hide Stats' : 'Statistics'}
              </Button>
              <Button
                variant={showAdmin ? 'primary' : 'ghost'}
                size="md"
                onClick={() => { setShowAdmin(v => !v); setShowStats(false); }}
              >
                <FiSettings size={14} />
                {showAdmin ? 'Exit Admin' : 'Manage'}
              </Button>
            </div>
          )}
        </div>

        {/* ── Toolbar ── */}
        <div className="tw-mb-5 sm:tw-mb-7 tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch sm:tw-items-center tw-gap-3 tw-flex-wrap tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-slate-200 tw-p-3">
          <div className="tw-relative tw-w-full sm:tw-flex-1 sm:tw-min-w-[260px]">
            <FaSearch className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400" size={13} />
            <input
              type="text"
              placeholder="Search version or title..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              className="tw-w-full tw-pl-10 tw-pr-9 tw-py-2.5 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-text-slate-700 tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition placeholder:tw-text-slate-400"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); fetchChangelog(''); }}
                className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-300 hover:tw-text-slate-500 tw-transition tw-border-0 tw-bg-transparent tw-cursor-pointer tw-outline-none"
              >
                <FaTimesCircle size={14} />
              </button>
            )}
          </div>

          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="tw-w-full sm:tw-w-auto tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-text-slate-700 tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition tw-cursor-pointer"
          >
            <option value="all">All Changes</option>
            <option value="features">Features only</option>
            <option value="fixes">Fixes only</option>
          </select>

          <div className="tw-w-full sm:tw-w-auto sm:tw-ml-auto">
            <Button variant="ghost" size="md" disabled={loading} onClick={() => fetchChangelog(search)}>
              <FaSync size={12} className={loading ? 'tw-animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ── View statistics (admin) ── */}
        {showStats && (
          <DocViewStatsPanel docType="changelog" title="Changelog — view statistics" />
        )}

        {/* ── Main grid ── */}
        <div className={`tw-grid tw-grid-cols-1 lg:tw-grid-cols-[270px_minmax(0,1fr)] tw-gap-4 lg:tw-gap-6 tw-items-start ${showStats ? 'tw-hidden' : ''}`}>

          {/* ── Sidebar ── */}
          {showAdmin ? (
            <ChangelogAdminPanel
              // Preview the entry in the detail panel WITHOUT leaving admin —
              // exiting admin on every card click was jarring. Works for drafts
              // too since the detail panel renders the entry object directly.
              onEntrySelect={entry => setSelectedEntry(entry)}
              onEntryUpdated={entry => { setSelectedEntry(entry); fetchChangelog(); }}
            />
          ) : (
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden lg:tw-sticky lg:tw-top-4 lg:tw-flex lg:tw-flex-col lg:tw-h-[calc(100dvh-2rem)]">
              <div className="tw-shrink-0 tw-px-4 md:tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-flex tw-items-center tw-gap-2.5">
                <span className="tw-w-2 tw-h-2 tw-bg-blue-500 tw-rounded-xl tw-shadow-lg tw-shadow-blue-500/50"></span>
                <p className="tw-text-xs tw-font-bold tw-text-slate-600 tw-uppercase tw-tracking-widest">
                  {filteredEntries.length} Release{filteredEntries.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="changelog-scroll tw-flex tw-max-h-[70vh] tw-overflow-x-auto tw-overflow-y-hidden lg:tw-block lg:tw-flex-1 lg:tw-min-h-0 lg:tw-max-h-none lg:tw-overflow-x-hidden lg:tw-overflow-y-auto">
                {loading && filteredEntries.length === 0 ? (
                  <LoadingSpinner size="sm" />
                ) : filteredEntries.length === 0 ? (
                  <p className="tw-text-sm tw-text-slate-400 tw-text-center tw-py-12">No results</p>
                ) : (
                  filteredEntries.map(entry => (
                    <VersionItem
                      key={entry.id}
                      entry={entry}
                      isActive={selectedEntry?.id === entry.id}
                      onClick={() => handleSelect(entry)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Detail panel ── */}
          {loading && !attrs ? (
            <LoadingSpinner size="lg" message="Loading changelog..." />
          ) : attrs ? (
            <div className="tw-space-y-4 sm:tw-space-y-5">

              {/* Release header */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-sm tw-p-4 sm:tw-p-6">
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-justify-between tw-gap-4">
                  <div className="tw-flex-1 tw-min-w-0 tw-break-words">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                      <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-slate-900 tw-font-mono">
                        v{fmtV(attrs.version)}
                      </h2>
                      {attrs.is_latest && (
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-bg-emerald-100 tw-text-emerald-700 tw-text-xs tw-font-bold tw-rounded-xl tw-border tw-border-emerald-200">
                          <FiStar size={11} className="tw-fill-emerald-400" /> Latest Release
                        </span>
                      )}
                    </div>
                    <p className="tw-text-xs tw-text-slate-400 tw-font-medium tw-mb-3">Released {attrs.release_date_formatted}</p>
                    <h3 className="tw-text-lg sm:tw-text-xl tw-leading-7 tw-font-bold tw-text-slate-800 tw-mb-2">{attrs.title}</h3>
                    {attrs.description && (
                      <div
                        className="tw-text-sm tw-text-slate-600 tw-leading-relaxed tw-break-words"
                        dangerouslySetInnerHTML={{ __html: attrs.description }}
                      />
                    )}
                  </div>
                  {attrs.upgrade_guide_url && (
                    <a
                      href={attrs.upgrade_guide_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-w-full sm:tw-w-auto tw-justify-center tw-shrink-0 tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-blue-700 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg hover:tw-bg-blue-100 tw-transition-colors"
                    >
                      Upgrade Guide <FaExternalLinkAlt size={11} />
                    </a>
                  )}
                </div>

                {/* Highlights */}
                {(attrs.highlights || []).length > 0 && (
                  <div className="tw-mt-5 tw-pt-5 tw-border-t tw-border-slate-200">
                    <p className="tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-3 tw-flex tw-items-center tw-gap-2">
                      <span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-bg-yellow-400 tw-rounded-xl"></span>
                      Highlights
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {attrs.highlights.map((h, idx) => (
                        <span key={idx} className="tw-px-3 tw-py-1.5 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-text-yellow-700 tw-text-xs tw-font-medium tw-rounded tw-inline-block">
                          ✨ {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Section cards — column count adapts to how many sections have data */}
              {(() => {
                const visible = SECTIONS.filter(s => (attrs[s.key] || []).length > 0);
                const colClass = visible.length === 1
                  ? 'tw-grid-cols-1'
                  : visible.length === 2
                    ? 'tw-grid-cols-1 md:tw-grid-cols-2'
                    : 'tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3';
                return (
                  <div className={`tw-grid ${colClass} tw-gap-5`}>
                    {SECTIONS.map(section => (
                      <SectionCard
                        key={section.key}
                        section={section}
                        items={attrs[section.key]}
                      />
                    ))}
                  </div>
                );
              })()}

            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-4">
              <div className="tw-text-slate-300 tw-text-4xl tw-mb-3">📋</div>
              <p className="tw-text-slate-500 tw-text-center tw-text-sm">Select a release from the left to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
